<template>
  <div
    ref="cardRef"
    class="review-card"
    :class="{ 'review-card--modal': props.forceExpand }"
  >
    <div
      ref="headerRef"
      class="review-card__header"
    >
      <div class="review-card__avatar">
        <LazyImage
          v-if="review.avatar"
          :src="review.avatar"
          :alt="review.name"
        />
        <TheAvatar v-else />
      </div>
      <div class="review-card__user-name">{{ review.name }}</div>
      <div class="review-card__rating">
        <Rating :value="review.rating" />
      </div>
      <div
        v-if="review.travelerLevel"
        class="review-card__experience-level"
      >
        {{ travelerLevelText }}
      </div>
      <div class="review-card__date">{{ formattedDate }}</div>
    </div>
    <a
      ref="titleRef"
      v-review-click-tracker="getReviewClickEventParams"
      class="review-card__title"
      :href="withoutDomain(review.link)"
      :target="titleLinkTarget"
      :class="{ 'review-card__title--truncated': isTitleTruncated }"
    >
      {{ review.title }}
    </a>
    <div
      ref="contentRef"
      class="review-card__content"
      :style="contentStyle"
    >
      <div class="review-card__text-wrapper">
        <div class="review-card__text-container">
          <div
            ref="textRef"
            class="review-card__text"
            lang="ru"
            :data-cursor-pointer="showMoreButton"
            @click="toggleCollapse"
          >
            {{ review.text }}
          </div>
        </div>
      </div>
      <button
        v-if="showMoreButton"
        class="review-card__more-button"
        :data-collapsed="isCollapsed"
        @click="toggleCollapse"
      >
        {{ t('review.more') }}
      </button>
    </div>
    <GalleryTrigger
      v-if="galleryPhotosList.length"
      v-once
      v-review-photo-click-tracker="getReviewPhotoClickEventParams"
      :caption-is-shown="review.photosInProcessing"
      :triggerPhotoWidth="56"
      :triggerPhotoHeight="56"
      :galleryPhotosList="galleryPhotosList"
      :title="review.title"
      :gallery-origin="galleryOrigin"
      @onCloseGallerySlider="emit('gallery-slider-closed')"
    />
  </div>
</template>

<script setup lang="ts">
  import { Rating } from '@/shared/ui/rating';
  import { LazyImage } from '@/shared/ui/lazyimage';
  import ru from '../i18n/ru.json';
  import { useI18n } from '@experience/i18n';
  import { TheAvatar } from '@/shared/ui/icons/avatar';
  import { choosePlural, useScreenSize, withoutDomain } from '@experience/shared';
  import { excursionCount } from '@/shared/consts';
  import { useResizeObserver } from '@vueuse/core';
  import { computed, nextTick, onMounted, ref, watch } from 'vue';
  import type { ReviewExperience, ReviewPhoto } from '@/shared/api/experience';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';
  import { addDays, differenceInHours, isBefore } from 'date-fns';
  import { getFromNowString, getLocalizedDateString } from '@experience/datetime-utils';
  import type { ReviewReplyInfo } from '@/shared/api/experience';
  import { useClickTracker } from '@/entities/sogu';
  import { GalleryOriginType, GalleryTrigger } from '@experience/gallery-slider';
  import { reviewPhotoToGalleryImg } from '../lib';
  import type { ISliderImg } from '@experience/gallery-slider/dist/components/GallerySlider';

  export interface Options {
    maxTitleLines: number;
    maxLines: number;
  }

  export interface ExperienceReview {
    id: number;
    title: string;
    link: string;
    name: string;
    avatar: string;
    rating: number;
    experienceId: number;
    experienceFormat: string;
    experienceType: string;
    travelerLevel: number;
    guide_reply: ReviewReplyInfo;
    date: string;
    text: string;
    photos: ReviewPhoto[];
    photosInProcessing: boolean;
    experience?: ReviewExperience;
    isDisabledExpand: boolean;
    options?: Options;
    defaultSoguFields?: ContextGeoSubject;
  }

  export interface ReviewCardProps {
    review: ExperienceReview;
    isDisabledExpand?: boolean;
    forceExpand?: boolean;
    options?: Options;
    defaultSoguFields: ContextGeoSubject;
  }

  const props = withDefaults(defineProps<ReviewCardProps>(), {
    isDisabledExpand: false,
    forceExpand: false,
    options: () => ({
      maxTitleLines: 3,
      maxLines: 6,
    }),
  });

  const emit = defineEmits<{
    'collapsed-content-clicked': [review: ExperienceReview];
    'toggle-collapse': [isCollapsed: boolean];
    'gallery-slider-closed': [void];
  }>();

  const PHOTOS_HEIGHT_AND_INDENTATION = 72;
  const PHOTOS_CAPTION_HEIGHT = 25;

  const { isMobile, isTablet } = useScreenSize();

  const review = props.review;
  const { t } = useI18n({ inheritLocale: true, messages: { ru } });

  const cardRef = ref<HTMLElement | null>(null);
  const headerRef = ref<HTMLElement | null>(null);
  const titleRef = ref<HTMLElement | null>(null);
  const textRef = ref<HTMLElement | null>(null);
  const contentRef = ref<HTMLElement | null>(null);

  const isCollapsed = ref(true);
  const _showMoreButton = ref(false);
  const maxTextHeight = ref(0);
  const userExpandedText = ref(false);
  const isTitleTruncated = ref(false);

  const titleLinkTarget = computed(() => {
    return isMobile.value || isTablet.value ? '_self' : '_blank';
  });

  const contentStyle = computed(() => {
    if (props.forceExpand) {
      return {};
    }

    return isCollapsed.value ? { maxHeight: `${maxTextHeight.value}px`, overflow: 'hidden' } : {};
  });

  const showMoreButton = computed(() => !props.forceExpand && _showMoreButton.value);

  const galleryPhotosList = computed<ISliderImg[]>(() =>
    review.photos.map((photo, index) => reviewPhotoToGalleryImg(photo, index)),
  );

  const galleryOrigin = computed(() => {
    const origin = {
      ...review,
      type: GalleryOriginType.REVIEW,
      avatar: {
        medium: review.avatar,
      },
      created_at: review.date,
      traveler_level: review.travelerLevel,
    };

    if (review.guide_reply?.text) {
      origin.guide_reply = review.guide_reply;
    }

    return origin;
  });

  const toggleCollapse = () => {
    if (!showMoreButton.value) {
      return;
    }
    if (props.isDisabledExpand) {
      emit('collapsed-content-clicked', review);

      return;
    }
    isCollapsed.value = !isCollapsed.value;
    userExpandedText.value = !isCollapsed.value;
    emit('toggle-collapse', isCollapsed.value);
  };

  const updateCollapse = async () => {
    await nextTick();
    if (!cardRef.value || !headerRef.value || !titleRef.value || !textRef.value || !contentRef.value) {
      return;
    }

    if (props.forceExpand) {
      isCollapsed.value = false;
      _showMoreButton.value = false;

      return;
    }

    const totalHeight = cardRef.value.clientHeight;
    const headerHeight = headerRef.value.clientHeight;
    const titleHeight = titleRef.value.clientHeight;
    const padding = 16;

    const photosHeight = review.photos.length ? PHOTOS_HEIGHT_AND_INDENTATION : 0;
    const photoCaptionIndent = review.photosInProcessing ? PHOTOS_CAPTION_HEIGHT : 0;

    const available = Math.max(
      totalHeight - (headerHeight + titleHeight + padding + photosHeight + photoCaptionIndent),
      0,
    );

    const computedTextStyle = getComputedStyle(textRef.value);
    const lineHeight = parseFloat(computedTextStyle.lineHeight);

    const maxLines = Math.floor(available / lineHeight);

    maxTextHeight.value = maxLines * lineHeight;

    const textHeight = textRef.value.scrollHeight;
    const needsCollapse = textHeight > available;

    isCollapsed.value = needsCollapse && !userExpandedText.value;
    _showMoreButton.value = needsCollapse;
  };

  const updateTitleTruncation = async () => {
    await nextTick();
    if (!titleRef.value) {
      return;
    }
    const style = getComputedStyle(titleRef.value);
    const lineHeight = parseFloat(style.lineHeight);
    const maxLines = review.options?.maxTitleLines ?? 3;
    const maxHeight = lineHeight * maxLines;
    const titleHeight = titleRef.value.scrollHeight;
    isTitleTruncated.value = titleHeight > maxHeight;
  };

  onMounted(() => {
    updateCollapse();
    updateTitleTruncation();
  });

  watch(
    () => [review.text, review.title, review.photos.length],
    () => {
      updateCollapse();
      updateTitleTruncation();
    },
  );

  useResizeObserver(cardRef, updateCollapse);
  useResizeObserver(textRef, updateCollapse);
  useResizeObserver(contentRef, updateCollapse);
  useResizeObserver(titleRef, updateTitleTruncation);

  const travelerLevelText = computed(
    () =>
      `${t('review.travelerLevel', { travelerLevel: review.travelerLevel })} ${choosePlural(review.travelerLevel, excursionCount.items_pluralized)}`,
  );

  const formattedDate = computed(() => {
    const [lastTime, nowTime] = [new Date(review.date), new Date()];
    const hours = differenceInHours(nowTime, lastTime);
    const PLURAL_HOURS = [t('time.hourAgo.one'), t('time.hourAgo.few'), t('time.hourAgo.many')];

    if (hours < 1) {
      return t('time.hourAgo.one');
    }

    if (hours < 24) {
      return `${hours} ${choosePlural(hours, PLURAL_HOURS)}`;
    }

    return isBefore(addDays(new Date(review.date), 5), new Date())
      ? getLocalizedDateString(review.date)
      : `${getFromNowString(review.date)} ${t('time.ago')}`;
  });

  const getReviewClickEventParams = () => ({
    ...props.defaultSoguFields.soguStamp,
    experience_id: review.experienceId,
  });

  const getReviewPhotoClickEventParams = () => ({
    ...getReviewClickEventParams(),
    count: review.photos.length,
  });

  const vReviewClickTracker = useClickTracker('listing:reviews.click');
  const vReviewPhotoClickTracker = useClickTracker('listing:reviews:photo.click-in-review.click');

  const gridTemplateAreas = computed(() =>
    review.travelerLevel
      ? `'avatar name name' 'avatar experience-level experience-level' 'rating rating date'`
      : `'avatar name name' 'rating rating date'`,
  );
  const gridTemplateAreasGreater320 = computed(() =>
    review.travelerLevel
      ? `'avatar name rating date' 'avatar experience-level experience-level experience-level'`
      : `'avatar name rating date'`,
  );
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as typography;

  .review-card__avatar {
    grid-area: avatar;
    width: 32px;
    height: 32px;
  }

  .review-card__avatar img {
    object-fit: cover;
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }

  .review-card__user-name {
    grid-area: name;
    overflow: hidden;
    color: var(--alias-color-text-default);
    text-transform: capitalize;
    text-wrap: nowrap;
    text-overflow: ellipsis;
    white-space: nowrap;

    @include typography.base-400-300-medium;
  }

  .review-card__rating {
    display: flex;
    grid-area: rating;
    align-items: center;
    margin-top: 8px;

    @container review-card (min-width: 320px) {
      justify-self: start;
      margin-top: 0;
    }
  }

  .review-card__experience-level {
    grid-area: experience-level;
    color: var(--alias-color-text-subtle);
    text-wrap: nowrap;

    @include typography.base-300-300-regular;
  }

  .review-card__date {
    grid-area: date;
    margin-top: 8px;
    color: var(--alias-color-text-subtle);

    @include typography.base-300-300-regular;

    @container review-card (min-width: 320px) {
      margin-top: 0;
      text-align: end;
    }
  }

  .review-card {
    position: relative;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    max-width: 100%;
    height: 100%;
    padding: 20px 0;
    container-type: inline-size;
    container-name: review-card;
  }

  .review-card--modal {
    padding-top: 0;
    padding-bottom: 0;
  }

  .review-card__header {
    display: grid;
    grid-template-areas: v-bind('gridTemplateAreas');
    grid-template-columns: 32px auto 1fr;
    align-content: center;
    align-items: center;
    column-gap: 6px;

    @container review-card (min-width: 320px) {
      grid-template-areas: v-bind('gridTemplateAreasGreater320');
      grid-template-columns: 32px auto 1fr auto;
      align-content: start;
      column-gap: 8px;
    }
  }

  .review-card__title {
    position: relative;
    margin: 8px 0 0;
    padding-right: 4px;
    color: var(--alias-color-link-default);
    text-decoration: none;
    overflow-wrap: break-word;

    @include typography.base-400-400-medium;

    &--truncated {
      display: -webkit-box;
      overflow: hidden;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
    }
  }

  .review-card__text-container {
    flex-grow: 1;
    overflow: hidden;
  }

  .review-card__text-wrapper {
    position: relative;
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    overflow: hidden;
    max-height: inherit;
  }

  .review-card__content {
    position: relative;
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    margin-bottom: auto;
  }

  .review-card__text {
    overflow: hidden;
    box-sizing: border-box;
    padding-right: 4px;
    color: var(--alias-color-text-default);
    white-space: pre-line;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    transition: max-height 0.1s ease;
    will-change: max-height;

    @include typography.base-400-400-regular;

    &[data-cursor-pointer='true'] {
      cursor: pointer;
    }
  }

  .review-card__text--collapsed {
    overflow: hidden;
  }

  :deep(.gallery-trigger) {
    justify-self: flex-end;
    margin-top: auto;
  }

  .review-card__more-button {
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 1;
    margin: 0;
    padding: 0 0 0 3px;
    border: none;
    background-color: var(--background);
    color: var(--alias-color-link-default);
    font-weight: var(--global-typography-weight-regular);
    font-size: var(--global-typography-size-400);
    line-height: var(--global-typography-line-height-400);
    cursor: pointer;

    @include typography.base-400-400-regular;

    &:before {
      content: '';
      position: absolute;
      right: 33px;
      bottom: 0;
      width: 33px;
      height: 24px;
      background: linear-gradient(270deg, var(--background) 0%, rgba(245, 247, 246, 0) 100%);
    }

    &[data-collapsed='false'],
    &[data-collapsed='false']:before {
      display: none;
    }
  }

  .show-more-gallery-trigger {
    margin-top: auto !important;
  }
</style>
